import { Download, Filter, History, RefreshCw, Search, X } from "lucide-react";
import React, { useMemo, useState } from "react";
import { IbkrPositionV2History, IbkrPositionV2HistoryData } from "../types";

interface PositionDiff {
  description: string;
  conid: number;
  type: "added" | "removed" | "increased" | "decreased" | "unchanged";
  positionBefore: number;
  positionAfter: number;
  change: number;
  changePercent: number;
  mktPriceBefore: number;
  mktPriceAfter: number;
  mktValueBefore: number;
  mktValueAfter: number;
  valueChange: number;
  avgCostBefore: number;
  avgCostAfter: number;
  realizedPnlBefore: number;
  realizedPnlAfter: number;
  unrealizedPnlBefore: number;
  unrealizedPnlAfter: number;
  currencyBefore: string | null;
  currencyAfter: string | null;
  secTypeBefore: string | null;
  secTypeAfter: string | null;
}

interface PositionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  positionHistory: IbkrPositionV2History[];
  positionHistoryCount: number;
  positionHistoryLoading: boolean;
  onLoadMore: (limit: number) => void;
}

const PositionHistoryModal: React.FC<PositionHistoryModalProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  positionHistory,
  positionHistoryCount,
  positionHistoryLoading,
  onLoadMore,
}) => {
  // Local state for modal
  const [selectedHistorySnapshot, setSelectedHistorySnapshot] = useState<IbkrPositionV2History | null>(null);
  const [diffMode, setDiffMode] = useState<boolean>(false);
  const [diffSnapshotA, setDiffSnapshotA] = useState<IbkrPositionV2History | null>(null);
  const [diffSnapshotB, setDiffSnapshotB] = useState<IbkrPositionV2History | null>(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<"all" | "added" | "removed" | "increased" | "decreased">("all");
  const [showUnchanged, setShowUnchanged] = useState<boolean>(false);

  // Filter helper for zero positions
  const filterNonZeroPositions = (data: IbkrPositionV2HistoryData[]) => {
    return data.filter(p => {
      const shares = Number(p.position) || 0;
      const mktValue = Number(p.mktValue) || 0;
      return shares !== 0 || mktValue !== 0;
    });
  };

  // Calculate diff between two snapshots (V2 compatible)
  const calculatePositionDiff = (
    snapshotA: IbkrPositionV2History | null,
    snapshotB: IbkrPositionV2History | null
  ): PositionDiff[] => {
    if (!snapshotA || !snapshotB) return [];

    const positionsA = new Map(snapshotA.data.map(p => [p.conid, p]));
    const positionsB = new Map(snapshotB.data.map(p => [p.conid, p]));
    const allConIds = new Set([...Array.from(positionsA.keys()), ...Array.from(positionsB.keys())]);

    const diffs: PositionDiff[] = [];

    allConIds.forEach(conId => {
      const posA = positionsA.get(conId);
      const posB = positionsB.get(conId);

      const positionBefore = posA ? Number(posA.position) : 0;
      const positionAfter = posB ? Number(posB.position) : 0;
      const mktValueBefore = posA ? Number(posA.mktValue) : 0;
      const mktValueAfter = posB ? Number(posB.mktValue) : 0;

      // Skip zero positions (where both shares and market value are zero on both sides)
      const beforeIsZero = positionBefore === 0 && mktValueBefore === 0;
      const afterIsZero = positionAfter === 0 && mktValueAfter === 0;
      if (beforeIsZero && afterIsZero) {
        return;
      }

      const change = positionAfter - positionBefore;
      const changePercent = positionBefore !== 0 ? (change / positionBefore) * 100 : positionAfter !== 0 ? 100 : 0;

      let type: PositionDiff["type"];
      if (!posA && posB) {
        type = "added";
      } else if (posA && !posB) {
        type = "removed";
      } else if (change > 0) {
        type = "increased";
      } else if (change < 0) {
        type = "decreased";
      } else {
        type = "unchanged";
      }

      diffs.push({
        description: posB?.description || posA?.description || `Contract ${conId}`,
        conid: conId,
        type,
        positionBefore,
        positionAfter,
        change,
        changePercent,
        mktPriceBefore: posA ? Number(posA.mktPrice) : 0,
        mktPriceAfter: posB ? Number(posB.mktPrice) : 0,
        mktValueBefore,
        mktValueAfter,
        valueChange: mktValueAfter - mktValueBefore,
        avgCostBefore: posA ? Number(posA.avgCost) : 0,
        avgCostAfter: posB ? Number(posB.avgCost) : 0,
        realizedPnlBefore: posA ? Number(posA.realizedPnl) : 0,
        realizedPnlAfter: posB ? Number(posB.realizedPnl) : 0,
        unrealizedPnlBefore: posA ? Number(posA.unrealizedPnl) : 0,
        unrealizedPnlAfter: posB ? Number(posB.unrealizedPnl) : 0,
        currencyBefore: posA?.currency || null,
        currencyAfter: posB?.currency || null,
        secTypeBefore: posA?.secType || null,
        secTypeAfter: posB?.secType || null,
      });
    });

    // Sort: added first, then removed, then changes (by absolute change), then unchanged
    const typeOrder = { added: 0, removed: 1, increased: 2, decreased: 3, unchanged: 4 };
    diffs.sort((a, b) => {
      if (typeOrder[a.type] !== typeOrder[b.type]) {
        return typeOrder[a.type] - typeOrder[b.type];
      }
      return Math.abs(b.change) - Math.abs(a.change);
    });

    return diffs;
  };

  // Get computed diff
  const positionDiffs = useMemo(
    () => (diffMode ? calculatePositionDiff(diffSnapshotA, diffSnapshotB) : []),
    [diffMode, diffSnapshotA, diffSnapshotB]
  );

  // Filter diffs based on search and type filter
  const filteredDiffs = useMemo(() => {
    let filtered = positionDiffs;

    // Apply type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(d => d.type === typeFilter);
    } else if (!showUnchanged) {
      filtered = filtered.filter(d => d.type !== "unchanged");
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        d =>
          (d.description || "").toLowerCase().includes(query) ||
          d.conid.toString().includes(query) ||
          (d.currencyAfter || "").toLowerCase().includes(query) ||
          (d.currencyBefore || "").toLowerCase().includes(query) ||
          (d.secTypeAfter || "").toLowerCase().includes(query) ||
          (d.secTypeBefore || "").toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [positionDiffs, typeFilter, showUnchanged, searchQuery]);

  const addedPositions = positionDiffs.filter(d => d.type === "added");
  const removedPositions = positionDiffs.filter(d => d.type === "removed");
  const increasedPositions = positionDiffs.filter(d => d.type === "increased");
  const decreasedPositions = positionDiffs.filter(d => d.type === "decreased");

  // CSV Export for diff
  const handleExportDiffCsv = () => {
    if (!diffSnapshotA || !diffSnapshotB) return;

    const escapeCsvValue = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    let csvContent = "";
    csvContent += "POSITION DIFF REPORT\n";
    csvContent += `Snapshot A (Before),${new Date(diffSnapshotA.createdAtUtc).toISOString()}\n`;
    csvContent += `Snapshot B (After),${new Date(diffSnapshotB.createdAtUtc).toISOString()}\n`;
    csvContent += `Total Changes,${filteredDiffs.length}\n`;
    csvContent += `Added,${addedPositions.length}\n`;
    csvContent += `Removed,${removedPositions.length}\n`;
    csvContent += `Increased,${increasedPositions.length}\n`;
    csvContent += `Decreased,${decreasedPositions.length}\n`;
    csvContent += "\n";

    csvContent += "CHANGES\n";
    const headers = [
      "Description",
      "ConID",
      "Change Type",
      "Currency",
      "Sec Type",
      "Position Before",
      "Position After",
      "Position Change",
      "Change %",
      "Avg Cost Before",
      "Avg Cost After",
      "Mkt Price Before",
      "Mkt Price After",
      "Mkt Value Before",
      "Mkt Value After",
      "Value Change",
      "Unrealized P&L Before",
      "Unrealized P&L After",
      "Realized P&L Before",
      "Realized P&L After",
    ];
    csvContent += headers.join(",") + "\n";

    filteredDiffs.forEach(diff => {
      const row = [
        escapeCsvValue(diff.description),
        diff.conid,
        diff.type,
        escapeCsvValue(diff.currencyAfter || diff.currencyBefore),
        escapeCsvValue(diff.secTypeAfter || diff.secTypeBefore),
        diff.positionBefore,
        diff.positionAfter,
        diff.change,
        diff.changePercent.toFixed(2),
        diff.avgCostBefore.toFixed(2),
        diff.avgCostAfter.toFixed(2),
        diff.mktPriceBefore.toFixed(2),
        diff.mktPriceAfter.toFixed(2),
        diff.mktValueBefore.toFixed(2),
        diff.mktValueAfter.toFixed(2),
        diff.valueChange.toFixed(2),
        diff.unrealizedPnlBefore.toFixed(2),
        diff.unrealizedPnlAfter.toFixed(2),
        diff.realizedPnlBefore.toFixed(2),
        diff.realizedPnlAfter.toFixed(2),
      ];
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const dateA = new Date(diffSnapshotA.createdAtUtc).toISOString().split("T")[0];
    const dateB = new Date(diffSnapshotB.createdAtUtc).toISOString().split("T")[0];
    link.setAttribute("download", `position_diff_${dateA}_to_${dateB}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // CSV Export for snapshot
  const handleExportSnapshotCsv = () => {
    if (!selectedHistorySnapshot) return;

    const escapeCsvValue = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const filteredData = filterNonZeroPositions(selectedHistorySnapshot.data);

    let csvContent = "";
    csvContent += "POSITION SNAPSHOT\n";
    csvContent += `Date,${new Date(selectedHistorySnapshot.createdAtUtc).toISOString()}\n`;
    csvContent += `Account,${selectedHistorySnapshot.ibkrAccountId}\n`;
    csvContent += `Total Positions,${filteredData.length}\n`;
    csvContent += "\n";

    csvContent += "POSITIONS\n";
    const headers = [
      "Description",
      "ConID",
      "Currency",
      "Sec Type",
      "Position",
      "Avg Cost",
      "Avg Price",
      "Mkt Price",
      "Mkt Value",
      "Unrealized P&L",
      "Realized P&L",
    ];
    csvContent += headers.join(",") + "\n";

    filteredData.forEach(pos => {
      const row = [
        escapeCsvValue(pos.description),
        pos.conid,
        escapeCsvValue(pos.currency),
        escapeCsvValue(pos.secType),
        Number(pos.position),
        (Number(pos.avgCost) || 0).toFixed(2),
        (Number(pos.avgPrice) || 0).toFixed(2),
        (Number(pos.mktPrice) || 0).toFixed(2),
        (Number(pos.mktValue) || 0).toFixed(2),
        (Number(pos.unrealizedPnl) || 0).toFixed(2),
        (Number(pos.realizedPnl) || 0).toFixed(2),
      ];
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const date = new Date(selectedHistorySnapshot.createdAtUtc).toISOString().split("T")[0];
    link.setAttribute("download", `position_snapshot_${date}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setSelectedHistorySnapshot(null);
    setDiffMode(false);
    setDiffSnapshotA(null);
    setDiffSnapshotB(null);
    setSearchQuery("");
    setTypeFilter("all");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
      <div
        className={`${
          isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        } border rounded-lg shadow-xl w-[98vw] max-w-[1800px] h-[95vh] flex flex-col`}
      >
        {/* Modal Header */}
        <div
          className={`flex items-center justify-between p-3 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <History className={`w-5 h-5 ${isDarkMode ? "text-amber-400" : "text-amber-600"}`} />
            <h2 className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              {diffMode ? "Compare Snapshots" : "Position History"}
            </h2>
            <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {positionHistoryCount > positionHistory.length
                ? `(showing ${positionHistory.length} of ${positionHistoryCount} snapshots)`
                : `(${positionHistory.length} snapshots)`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Diff Mode Toggle */}
            <button
              onClick={() => {
                setDiffMode(!diffMode);
                setSearchQuery("");
                setTypeFilter("all");
                if (!diffMode) {
                  setSelectedHistorySnapshot(null);
                } else {
                  setDiffSnapshotA(null);
                  setDiffSnapshotB(null);
                }
              }}
              className={`px-3 py-1.5 rounded text-sm font-mono transition-colors ${
                diffMode
                  ? isDarkMode
                    ? "bg-gray-700 text-gray-200 border border-gray-600"
                    : "bg-gray-200 text-gray-800 border border-gray-300"
                  : isDarkMode
                  ? "bg-gray-700/50 text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              }`}
            >
              {diffMode ? "exit diff" : "git diff"}
            </button>
            <button
              onClick={handleClose}
              className={`p-1.5 rounded-lg transition-colors ${
                isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left: History List */}
          <div
            className={`w-56 flex-shrink-0 border-r ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            } overflow-y-auto flex flex-col`}
          >
            {/* Diff Mode Selection Header - Git style */}
            {diffMode && (
              <div
                className={`p-3 border-b ${
                  isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
                }`}
              >
                <div className={`text-xs font-mono mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  Select commits to compare:
                </div>
                <div className="space-y-1 font-mono">
                  <div className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    <span className="inline-block w-8 text-rose-600">a:</span>
                    {diffSnapshotA ? (
                      <span className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                        {new Date(diffSnapshotA.createdAtUtc).toISOString().split("T")[0]}
                      </span>
                    ) : (
                      <span className={isDarkMode ? "text-gray-600" : "text-gray-400"}>---</span>
                    )}
                  </div>
                  <div className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    <span className="inline-block w-8 text-emerald-600">b:</span>
                    {diffSnapshotB ? (
                      <span className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                        {new Date(diffSnapshotB.createdAtUtc).toISOString().split("T")[0]}
                      </span>
                    ) : (
                      <span className={isDarkMode ? "text-gray-600" : "text-gray-400"}>---</span>
                    )}
                  </div>
                </div>
                {diffSnapshotA && diffSnapshotB && (
                  <button
                    onClick={() => {
                      setDiffSnapshotA(null);
                      setDiffSnapshotB(null);
                    }}
                    className={`mt-2 text-xs font-mono ${
                      isDarkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    reset
                  </button>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              {positionHistoryLoading ? (
                <div className="p-4 text-center">
                  <RefreshCw
                    className={`w-5 h-5 animate-spin mx-auto ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                  <p className={`text-sm mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>Loading history...</p>
                </div>
              ) : positionHistory.length === 0 ? (
                <div className="p-4 text-center">
                  <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    No position history available
                  </p>
                </div>
              ) : (
                positionHistory.map(snapshot => {
                  const isSnapshotA = diffSnapshotA?.id === snapshot.id;
                  const isSnapshotB = diffSnapshotB?.id === snapshot.id;
                  const isSelected = selectedHistorySnapshot?.id === snapshot.id;

                  return (
                    <button
                      key={snapshot.id}
                      onClick={() => {
                        if (diffMode) {
                          if (!diffSnapshotA) {
                            setDiffSnapshotA(snapshot);
                          } else if (!diffSnapshotB && snapshot.id !== diffSnapshotA.id) {
                            const aTime = new Date(diffSnapshotA.createdAtUtc).getTime();
                            const bTime = new Date(snapshot.createdAtUtc).getTime();
                            if (aTime > bTime) {
                              setDiffSnapshotB(diffSnapshotA);
                              setDiffSnapshotA(snapshot);
                            } else {
                              setDiffSnapshotB(snapshot);
                            }
                          } else if (isSnapshotA) {
                            setDiffSnapshotA(null);
                          } else if (isSnapshotB) {
                            setDiffSnapshotB(null);
                          }
                        } else {
                          setSelectedHistorySnapshot(snapshot);
                        }
                      }}
                      className={`w-full text-left p-2 border-b transition-colors relative ${
                        isDarkMode
                          ? `border-gray-700 ${
                              isSnapshotA
                                ? "bg-rose-950/40 border-l-2 border-l-rose-700"
                                : isSnapshotB
                                ? "bg-emerald-950/40 border-l-2 border-l-emerald-700"
                                : isSelected
                                ? "bg-gray-700/50"
                                : "hover:bg-gray-700/30"
                            }`
                          : `border-gray-100 ${
                              isSnapshotA
                                ? "bg-rose-50 border-l-2 border-l-rose-600"
                                : isSnapshotB
                                ? "bg-emerald-50 border-l-2 border-l-emerald-600"
                                : isSelected
                                ? "bg-gray-100"
                                : "hover:bg-gray-50"
                            }`
                      }`}
                    >
                      {diffMode && (isSnapshotA || isSnapshotB) && (
                        <span
                          className={`absolute top-1 right-1 text-xs font-mono font-bold px-1 py-0.5 rounded ${
                            isSnapshotA
                              ? isDarkMode
                                ? "bg-rose-900 text-rose-300"
                                : "bg-rose-100 text-rose-700"
                              : isDarkMode
                              ? "bg-emerald-900 text-emerald-300"
                              : "bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          {isSnapshotA ? "a" : "b"}
                        </span>
                      )}
                      <div className={`text-xs font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                        {new Date(snapshot.createdAtUtc).toLocaleDateString()}
                      </div>
                      <div className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                        {new Date(snapshot.createdAtUtc).toLocaleTimeString()}
                      </div>
                      <div className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                        {filterNonZeroPositions(snapshot.data).length} positions
                      </div>
                    </button>
                  );
                })
              )}
              {positionHistoryCount > positionHistory.length && !positionHistoryLoading && (
                <button
                  onClick={() => onLoadMore(positionHistory.length + 50)}
                  className={`w-full p-2 text-center text-xs font-medium transition-colors ${
                    isDarkMode ? "text-amber-400 hover:bg-gray-700/50" : "text-amber-600 hover:bg-gray-50"
                  }`}
                >
                  Load More ({positionHistoryCount - positionHistory.length} remaining)
                </button>
              )}
            </div>
          </div>

          {/* Right: Snapshot Details or Diff View */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {diffMode ? (
              diffSnapshotA && diffSnapshotB ? (
                <>
                  {/* Diff Header with Search/Filter/Export */}
                  <div className={`p-3 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} flex-shrink-0`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-4">
                        <h3 className={`text-sm font-mono ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                          diff --positions
                        </h3>
                        <div className={`text-xs font-mono ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
                          {new Date(diffSnapshotA.createdAtUtc).toISOString().split("T")[0]}
                          {".."}
                          {new Date(diffSnapshotB.createdAtUtc).toISOString().split("T")[0]}
                        </div>
                      </div>
                      <button
                        onClick={handleExportDiffCsv}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border transition-colors ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600 text-gray-300 hover:border-teal-500 hover:text-teal-300"
                            : "bg-white border-gray-300 text-gray-700 hover:border-teal-500 hover:text-teal-600"
                        }`}
                      >
                        <Download className="w-3.5 h-3.5" />
                        Export CSV
                      </button>
                    </div>

                    {/* Summary Stats */}
                    <div
                      className={`flex items-center gap-4 text-xs font-mono mb-3 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      <span className="text-emerald-600">+{addedPositions.length} added</span>
                      <span className="text-rose-600">-{removedPositions.length} removed</span>
                      <span className="text-blue-500">+{increasedPositions.length} increased</span>
                      <span className="text-orange-500">-{decreasedPositions.length} decreased</span>
                    </div>

                    {/* Search and Filters */}
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1 max-w-md">
                        <Search
                          className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        />
                        <input
                          type="text"
                          placeholder="Search by description, ConID, currency, type..."
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          className={`w-full pl-8 pr-3 py-1.5 text-xs rounded border ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                          } focus:outline-none focus:ring-1 focus:ring-teal-500`}
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery("")}
                            className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
                              isDarkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
                            }`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Filter className={`w-3.5 h-3.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
                        <select
                          value={typeFilter}
                          onChange={e => setTypeFilter(e.target.value as typeof typeFilter)}
                          className={`px-2 py-1.5 text-xs rounded border ${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-gray-300"
                              : "bg-white border-gray-300 text-gray-700"
                          }`}
                        >
                          <option value="all">All Changes</option>
                          <option value="added">Added Only</option>
                          <option value="removed">Removed Only</option>
                          <option value="increased">Increased Only</option>
                          <option value="decreased">Decreased Only</option>
                        </select>
                      </div>
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={showUnchanged}
                          onChange={e => setShowUnchanged(e.target.checked)}
                          className="rounded"
                        />
                        <span className={isDarkMode ? "text-gray-400" : "text-gray-600"}>Show unchanged</span>
                      </label>
                      <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                        {filteredDiffs.length} results
                      </span>
                    </div>
                  </div>

                  {/* Diff Table */}
                  <div className="flex-1 overflow-auto p-3">
                    <table className="w-full text-xs border-collapse min-w-[1200px]">
                      <thead className={`sticky top-0 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
                        <tr
                          className={`${
                            isDarkMode
                              ? "text-gray-400 border-b border-gray-700"
                              : "text-gray-600 border-b border-gray-200"
                          }`}
                        >
                          <th className="text-left py-2 px-2 font-medium w-6"></th>
                          <th className="text-left py-2 px-2 font-medium">Description</th>
                          <th className="text-center py-2 px-2 font-medium">Currency</th>
                          <th className="text-center py-2 px-2 font-medium">Type</th>
                          <th className="text-right py-2 px-2 font-medium">Pos Before</th>
                          <th className="text-right py-2 px-2 font-medium">Pos After</th>
                          <th className="text-right py-2 px-2 font-medium">Change</th>
                          <th className="text-right py-2 px-2 font-medium">%</th>
                          <th className="text-right py-2 px-2 font-medium">Avg Cost</th>
                          <th className="text-right py-2 px-2 font-medium">Mkt Value Before</th>
                          <th className="text-right py-2 px-2 font-medium">Mkt Value After</th>
                          <th className="text-right py-2 px-2 font-medium">Value Change</th>
                          <th className="text-right py-2 px-2 font-medium">Unreal P&L Before</th>
                          <th className="text-right py-2 px-2 font-medium">Unreal P&L After</th>
                          <th className="text-right py-2 px-2 font-medium">ConId</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDiffs.map(diff => (
                          <tr
                            key={diff.conid}
                            className={`border-l-2 ${
                              diff.type === "added"
                                ? isDarkMode
                                  ? "bg-emerald-950/20 border-l-emerald-600"
                                  : "bg-emerald-50/50 border-l-emerald-500"
                                : diff.type === "removed"
                                ? isDarkMode
                                  ? "bg-rose-950/20 border-l-rose-600"
                                  : "bg-rose-50/50 border-l-rose-500"
                                : diff.type === "increased"
                                ? isDarkMode
                                  ? "bg-blue-950/20 border-l-blue-600"
                                  : "bg-blue-50/50 border-l-blue-500"
                                : diff.type === "decreased"
                                ? isDarkMode
                                  ? "bg-orange-950/20 border-l-orange-600"
                                  : "bg-orange-50/50 border-l-orange-500"
                                : isDarkMode
                                ? "border-l-gray-700"
                                : "border-l-gray-300"
                            }`}
                          >
                            <td className="py-1.5 px-2">
                              <span
                                className={`font-bold ${
                                  diff.type === "added" || diff.type === "increased"
                                    ? "text-emerald-500"
                                    : diff.type === "removed" || diff.type === "decreased"
                                    ? "text-rose-500"
                                    : isDarkMode
                                    ? "text-gray-600"
                                    : "text-gray-400"
                                }`}
                              >
                                {diff.type === "added" || diff.type === "increased"
                                  ? "+"
                                  : diff.type === "removed" || diff.type === "decreased"
                                  ? "-"
                                  : "="}
                              </span>
                            </td>
                            <td className={`py-1.5 px-2 font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                              {diff.description}
                              {(diff.type === "added" || diff.type === "removed") && (
                                <span className={`ml-1.5 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                                  ({diff.type})
                                </span>
                              )}
                            </td>
                            <td className={`py-1.5 px-2 text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                              {diff.currencyAfter || diff.currencyBefore || "-"}
                            </td>
                            <td className={`py-1.5 px-2 text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                              {diff.secTypeAfter || diff.secTypeBefore || "-"}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              {diff.positionBefore === 0 ? "-" : diff.positionBefore.toLocaleString()}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {diff.positionAfter === 0 ? "-" : diff.positionAfter.toLocaleString()}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                diff.change > 0 ? "text-emerald-500" : diff.change < 0 ? "text-rose-500" : ""
                              }`}
                            >
                              {diff.change > 0 ? "+" : ""}
                              {diff.change.toLocaleString()}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono text-xs ${
                                diff.change > 0 ? "text-emerald-500" : diff.change < 0 ? "text-rose-500" : ""
                              }`}
                            >
                              {diff.type === "added"
                                ? "new"
                                : diff.type === "removed"
                                ? "exit"
                                : `${diff.changePercent > 0 ? "+" : ""}${diff.changePercent.toFixed(1)}%`}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {diff.avgCostAfter > 0 ? diff.avgCostAfter.toFixed(2) : "-"}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              {diff.mktValueBefore === 0
                                ? "-"
                                : diff.mktValueBefore.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {diff.mktValueAfter === 0
                                ? "-"
                                : diff.mktValueAfter.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                diff.valueChange > 0 ? "text-emerald-500" : diff.valueChange < 0 ? "text-rose-500" : ""
                              }`}
                            >
                              {diff.valueChange > 0 ? "+" : ""}
                              {diff.valueChange.toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              {diff.unrealizedPnlBefore === 0
                                ? "-"
                                : diff.unrealizedPnlBefore.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                diff.unrealizedPnlAfter >= 0 ? "text-emerald-500" : "text-rose-500"
                              }`}
                            >
                              {diff.unrealizedPnlAfter === 0
                                ? "-"
                                : `${diff.unrealizedPnlAfter >= 0 ? "+" : ""}${diff.unrealizedPnlAfter.toLocaleString(
                                    undefined,
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}`}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-600" : "text-gray-400"
                              }`}
                            >
                              {diff.conid}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {filteredDiffs.length === 0 && (
                      <div
                        className={`text-center py-12 font-mono text-sm ${
                          isDarkMode ? "text-gray-600" : "text-gray-400"
                        }`}
                      >
                        {searchQuery || typeFilter !== "all"
                          ? "No matching changes found"
                          : "No changes detected between snapshots"}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                      Select two snapshots to compare
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                      Click snapshots on the left to select "Before" and "After"
                    </p>
                  </div>
                </div>
              )
            ) : selectedHistorySnapshot ? (
              <>
                {/* Snapshot Header */}
                <div className={`p-3 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} flex-shrink-0`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                        Snapshot from {new Date(selectedHistorySnapshot.createdAtUtc).toLocaleString()}
                      </h3>
                      <p className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                        Account: {selectedHistorySnapshot.ibkrAccountId} |{" "}
                        {filterNonZeroPositions(selectedHistorySnapshot.data).length} positions
                      </p>
                    </div>
                    <button
                      onClick={handleExportSnapshotCsv}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded border transition-colors ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-gray-300 hover:border-teal-500 hover:text-teal-300"
                          : "bg-white border-gray-300 text-gray-700 hover:border-teal-500 hover:text-teal-600"
                      }`}
                    >
                      <Download className="w-3.5 h-3.5" />
                      Export CSV
                    </button>
                  </div>
                </div>

                {/* Snapshot Table */}
                <div className="flex-1 overflow-auto p-3">
                  <table className="w-full text-xs border-collapse min-w-[900px]">
                    <thead className={`sticky top-0 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
                      <tr
                        className={`${
                          isDarkMode
                            ? "text-gray-400 border-b border-gray-700"
                            : "text-gray-600 border-b border-gray-200"
                        }`}
                      >
                        <th className="text-left py-2 px-2 font-medium">Description</th>
                        <th className="text-center py-2 px-2 font-medium">Currency</th>
                        <th className="text-center py-2 px-2 font-medium">Type</th>
                        <th className="text-right py-2 px-2 font-medium">Position</th>
                        <th className="text-right py-2 px-2 font-medium">Avg Cost</th>
                        <th className="text-right py-2 px-2 font-medium">Mkt Price</th>
                        <th className="text-right py-2 px-2 font-medium">Mkt Value</th>
                        <th className="text-right py-2 px-2 font-medium">Unrealized P&L</th>
                        <th className="text-right py-2 px-2 font-medium">Realized P&L</th>
                        <th className="text-right py-2 px-2 font-medium">ConId</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterNonZeroPositions(selectedHistorySnapshot.data).map((pos, idx) => {
                        const unrealizedPnl = Number(pos.unrealizedPnl) || 0;
                        return (
                          <tr
                            key={`${pos.conid}-${idx}`}
                            className={`border-b ${
                              isDarkMode ? "border-gray-700 hover:bg-gray-700/30" : "border-gray-100 hover:bg-gray-50"
                            }`}
                          >
                            <td className={`py-1.5 px-2 font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                              {pos.description || `ConID: ${pos.conid}`}
                            </td>
                            <td className={`py-1.5 px-2 text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                              {pos.currency || "-"}
                            </td>
                            <td className={`py-1.5 px-2 text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                              {pos.secType || "-"}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {Number(pos.position).toLocaleString()}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              ${Number(pos.avgCost).toFixed(2)}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              ${Number(pos.mktPrice).toFixed(2)}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              $
                              {Number(pos.mktValue).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                unrealizedPnl >= 0 ? "text-emerald-500" : "text-rose-500"
                              }`}
                            >
                              {unrealizedPnl >= 0 ? "+" : ""}$
                              {unrealizedPnl.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              $
                              {Number(pos.realizedPnl).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td
                              className={`py-1.5 px-2 text-right font-mono ${
                                isDarkMode ? "text-gray-600" : "text-gray-400"
                              }`}
                            >
                              {pos.conid}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                  Select a snapshot from the list to view details
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionHistoryModal;
